// =============================================================================
// SDK UTILITIES
// Helper functions for the AllBrainer SDK
// =============================================================================

import type { UTMParams, AttributionRecord } from './types';
import { STORAGE_KEY as VISITOR_STORAGE_KEY } from './identity';

// In-memory fallback used when localStorage is unavailable (e.g., private browsing)
let _inMemoryAttribution: (AttributionRecord & { timestamp: string; expires: string }) | null = null;

function _readAttrRaw(): (AttributionRecord & { timestamp: string; expires: string }) | null {
  try {
    const stored = localStorage.getItem('_ab_attr');
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return _inMemoryAttribution;
  }
}

function _writeAttrRaw(data: AttributionRecord & { timestamp: string; expires: string }): void {
  try {
    localStorage.setItem('_ab_attr', JSON.stringify(data));
    _inMemoryAttribution = null;
  } catch {
    _inMemoryAttribution = data;
  }
}

function _removeAttrRaw(): void {
  try {
    localStorage.removeItem('_ab_attr');
  } catch {
    _inMemoryAttribution = null;
  }
}

/**
 * Generate a unique visitor ID
 */
export function generateVisitorId(): string {
  // Use crypto.randomUUID if available (browser + Node 19+)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate a unique session ID
 */
export function generateSessionId(): string {
  return `sess_${Date.now()}_${generateVisitorId().split('-')[0]}`;
}

/**
 * Generate a unique event ID
 */
export function generateEventId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Parse UTM parameters from URL
 */
export function parseUTMParams(url?: string): UTMParams {
  const searchParams = new URLSearchParams(
    url ? new URL(url).search : (typeof window !== 'undefined' ? window.location.search : '')
  );

  const utm: UTMParams = {};

  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

  for (const key of utmKeys) {
    const value = searchParams.get(key);
    if (value) {
      utm[key] = value;
    }
  }

  return utm;
}

/**
 * Parse full attribution params from URL: UTM fields + click IDs + ad-variant params
 */
export function parseAttributionParams(url?: string): AttributionRecord {
  const searchParams = new URLSearchParams(
    url ? new URL(url).search : (typeof window !== 'undefined' ? window.location.search : '')
  );

  const attr: AttributionRecord = {};

  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
  for (const key of utmKeys) {
    const value = searchParams.get(key);
    if (value) attr[key] = value;
  }

  const extKeys = ['gclid', 'fbclid', 'msclkid', 'li_fat_id', 'ttclid', 'ab_project', 'ab_variant'] as const;
  for (const key of extKeys) {
    const value = searchParams.get(key);
    if (value) attr[key] = value;
  }

  return attr;
}

/**
 * Detect device type from user agent
 */
export function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof navigator === 'undefined') {
    return 'desktop';
  }

  const ua = navigator.userAgent.toLowerCase();

  if (/mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua)) {
    return 'mobile';
  }

  if (/ipad|android(?!.*mobile)|tablet/i.test(ua)) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * Get stored visitor ID from cookie or localStorage.
 *
 * Reads the same storage key the LP SDK writes (`identity.ts`), so a visitor
 * assigned an LP variant and a visitor tracked via events share one identity.
 */
export function getStoredVisitorId(preferLocalStorage = false): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const key = VISITOR_STORAGE_KEY;

  // Try localStorage first if preferred
  if (preferLocalStorage) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) return stored;
    } catch {
      // localStorage might be disabled
    }
  }

  // Try cookies
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === key && value) {
      return decodeURIComponent(value);
    }
  }

  // Fallback to localStorage if not preferring it
  if (!preferLocalStorage) {
    try {
      return localStorage.getItem(key);
    } catch {
      // localStorage might be disabled
    }
  }

  return null;
}

/**
 * Store visitor ID in cookie and/or localStorage.
 *
 * Writes the same storage key the LP SDK reads (`identity.ts`), so a visitor
 * assigned an LP variant and a visitor tracked via events share one identity.
 */
export function storeVisitorId(
  visitorId: string,
  options: { domain?: string; preferLocalStorage?: boolean; maxAgeDays?: number } = {}
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const key = VISITOR_STORAGE_KEY;
  const maxAge = (options.maxAgeDays ?? 365) * 24 * 60 * 60;

  // Store in localStorage
  try {
    localStorage.setItem(key, visitorId);
  } catch {
    // localStorage might be disabled
  }

  // Store in cookie
  let cookie = `${key}=${encodeURIComponent(visitorId)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  if (options.domain) {
    cookie += `; domain=${options.domain}`;
  }
  document.cookie = cookie;
}

/**
 * Store session ID (session-only storage)
 */
export function storeSessionId(sessionId: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem('_ab_sid', sessionId);
  } catch {
    // sessionStorage might be disabled
  }
}

/**
 * Get stored session ID
 */
export function getStoredSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return sessionStorage.getItem('_ab_sid');
  } catch {
    return null;
  }
}

/**
 * Store attribution with first-touch preservation and last-touch click ID updates.
 * First-touch fields (UTMs, click IDs, ab_project/ab_variant) are written only
 * when absent from the stored record. Click IDs are additionally mirrored into
 * lastGclid/lastFbclid/lastMsclkid/lastLiFatId/lastTtclid on every call so
 * platforms always receive the freshest click ID for conversion upload.
 */
export function storeAttribution(
  attribution: AttributionRecord,
  options: { maxAgeDays?: number } = {}
): void {
  if (typeof window === 'undefined' || Object.keys(attribution).length === 0) {
    return;
  }

  const maxAgeDays = options.maxAgeDays ?? 30;

  // Read existing stored record for first-touch preservation
  let existing: AttributionRecord | null = null;
  const raw = _readAttrRaw();
  if (raw) {
    const exp = new Date(raw.expires);
    if (exp >= new Date()) {
      const { timestamp: _ts, expires: _exp, ...rest } = raw;
      existing = rest as AttributionRecord;
    }
  }

  const merged: AttributionRecord = existing ? { ...existing } : {};

  // First-touch: only write fields that are not yet stored
  const firstTouchKeys: (keyof AttributionRecord)[] = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'msclkid', 'li_fat_id', 'ttclid', 'ab_project', 'ab_variant',
  ];
  for (const k of firstTouchKeys) {
    if (merged[k] == null && attribution[k] != null) {
      (merged as Record<string, string | undefined>)[k as string] = attribution[k];
    }
  }

  // Last-touch click IDs: always update from the incoming visit
  if (attribution.gclid) merged.lastGclid = attribution.gclid;
  if (attribution.fbclid) merged.lastFbclid = attribution.fbclid;
  if (attribution.msclkid) merged.lastMsclkid = attribution.msclkid;
  if (attribution.li_fat_id) merged.lastLiFatId = attribution.li_fat_id;
  if (attribution.ttclid) merged.lastTtclid = attribution.ttclid;

  _writeAttrRaw({
    ...merged,
    timestamp: new Date().toISOString(),
    expires: new Date(Date.now() + maxAgeDays * 24 * 60 * 60 * 1000).toISOString(),
  });
}

/**
 * Get stored attribution data (returns full AttributionRecord including click IDs)
 */
export function getStoredAttribution(): AttributionRecord | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = _readAttrRaw();
  if (!raw) return null;

  const expires = new Date(raw.expires);
  if (expires < new Date()) {
    _removeAttrRaw();
    return null;
  }

  const { timestamp: _ts, expires: _exp, ...attr } = raw;
  return attr as AttributionRecord;
}

/**
 * Check if we're in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
